import Layout from "@/components/application/layout";
import { ReactNode } from "react";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Pie,
  PieChart,
  XAxis,
  YAxis,
} from "recharts";
import { usePage } from "@inertiajs/react";
import { AdminAnalyticsPageProps } from "@/types";
import useTranslations from "@/hooks/use-translations";

function useVisitsChartConfig(): ChartConfig {
  const { t } = useTranslations("admin.stores.analytics.chart");

  return {
    visits: {
      label: t("visits"),
      color: "var(--chart-2)",
    },
  };
}

function useVisitsByDeviceTypeChartConfig(): ChartConfig {
  const { t } = useTranslations("admin.stores.analytics.chart");

  return {
    visits: {
      label: t("visits"),
    },
    desktop: {
      label: t("desktop"),
      color: "var(--chart-2)",
    },
    mobile: {
      label: t("mobile"),
      color: "var(--chart-3)",
    },
    tablet: {
      label: t("tablet"),
      color: "var(--chart-3)",
    },
    phablet: {
      label: t("phablet"),
      color: "var(--chart-4)",
    },
  };
}

function StoreAnalyticsPage() {
  const { visits, by_device_type } = usePage<AdminAnalyticsPageProps>().props;
  const { t } = useTranslations("admin.stores.analytics");
  const visitsChartConfig = useVisitsChartConfig();
  const visitsByDeviceTypeChartConfig = useVisitsByDeviceTypeChartConfig();

  const visitsChartData = Object.entries(visits).map(([date, visits]) => ({
    date,
    visits,
  }));
  const visitsByDeviceTypeChartData = Object.entries(by_device_type).map(
    ([deviceType, visits]) => ({
      device_type: deviceType.toLocaleLowerCase(),
      visits,
      fill: `var(--color-${deviceType.toLocaleLowerCase()})`,
    })
  );

  return (
    <div className="flex flex-col gap-8">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold">{t("title")}</h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="flex flex-col gap-5 col-span-1 lg:col-span-2">
          <h2 className="text-lg font-semibold">{t("visits_title")}</h2>

          <ChartContainer
            config={visitsChartConfig}
            className="aspect-auto h-40 lg:h-64 w-full"
          >
            <AreaChart
              accessibilityLayer
              data={visitsChartData}
              margin={{
                left: -24,
              }}
            >
              <defs>
                <linearGradient id="fillVisits" x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="5%"
                    stopColor="var(--color-visits)"
                    stopOpacity={0.8}
                  />
                  <stop
                    offset="95%"
                    stopColor="var(--color-visits)"
                    stopOpacity={0.1}
                  />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="date"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                minTickGap={32}
                tickFormatter={(value) => {
                  const date = new Date(value);
                  return date.toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  });
                }}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tickCount={3}
              />
              <ChartTooltip cursor={false} content={<ChartTooltipContent />} />
              <Area
                dataKey="visits"
                type="natural"
                fill="url(#fillVisits)"
                fillOpacity={0.4}
                stroke="var(--color-visits)"
                stackId="a"
              />
            </AreaChart>
          </ChartContainer>
        </div>

        <div className="flex flex-col gap-5">
          <h2 className="text-lg font-semibold">
            {t("visits_by_device_type_title")}
          </h2>

          <ChartContainer
            config={visitsByDeviceTypeChartConfig}
            className="aspect-auto h-64 w-full"
          >
            <PieChart>
              <ChartTooltip
                cursor={false}
                content={<ChartTooltipContent hideLabel />}
              />
              <Pie
                data={visitsByDeviceTypeChartData}
                dataKey="visits"
                nameKey="device_type"
                innerRadius="55%"
              />
            </PieChart>
          </ChartContainer>
        </div>
      </div>
    </div>
  );
}

StoreAnalyticsPage.layout = (page: ReactNode) => <Layout children={page} />;

export default StoreAnalyticsPage;
